import { FC, useState } from "react";
import { ChevronRightIcon } from "lucide-react-native";

import {
  CoverageDetailsType,
  CoverageStatusEnumType,
  MemberCoverageType,
} from "@libs/api/patients";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnLink } from "@/components/VnLink";
import { VnSheet, VnSheetContent } from "@/components/VnSheet";
import { VnText } from "@/components/VnText/VnText";
import { HouseholdMemberType } from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { getApplicationTypeLabel } from "@/utils/applications";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

import { CoverageStatusIcon } from "./CoverageStatusIcon";
import { SideSheetHeader } from "./SideSheetHeader";

const infoClause = (header: string, value: string) => (
  <VnBox className="flex-col items-start gap-0.5">
    <VnText variant="lg" className="text-muted-foreground">
      {header}
    </VnText>
    <VnText variant="lg">{value}</VnText>
  </VnBox>
);

type CoverageDetailsPropsType = {
  coverage: CoverageDetailsType;
};

const CoverageDetails: FC<CoverageDetailsPropsType> = ({ coverage }) => {
  const { t } = useTranslator();

  return (
    <VnBox className="w-full flex-col gap-4">
      <VnCard className="items-start gap-4">
        <>
          {coverage.plan &&
            infoClause(t("native:coverage.planName"), coverage.plan.name)}
          {coverage.primaryCareProvider &&
            infoClause(
              t("native:coverage.primaryCareProvider"),
              coverage.primaryCareProvider.name,
            )}
          {infoClause(
            t("native:coverage.lastCheck"),
            coverage.checkCompletionDate
              ? dateAsDayjs(coverage.checkCompletionDate).format(
                  t("native:coverage.lastCheckDateFormat"),
                )
              : t("common:unknown"),
          )}
        </>
      </VnCard>

      {coverage.serviceTypes.length > 0 && (
        <VnCard className="items-start gap-4">
          <VnText variant="lgMedium" i18nKey="native:coverage.serviceTypes" />
          {coverage.serviceTypes.map((serviceType, index) => (
            <VnBox
              key={`${serviceType.code}-${index}`}
              className="w-full flex-col items-start gap-0.5">
              <VnBox
                className={cn(
                  "flex w-full flex-row justify-between",
                  !serviceType.description && "mb-6",
                )}>
                <VnText variant="lg">{serviceType.name}</VnText>
                <VnBox className="flex flex-row gap-1">
                  <VnText
                    variant="lg"
                    className="text-muted-foreground"
                    i18nKey="native:coverage.code"
                  />
                  <VnText variant="lg">{serviceType.code}</VnText>
                </VnBox>
              </VnBox>
              {serviceType.description && (
                <VnText variant="lg" className="text-muted-foreground">
                  {serviceType.description}
                </VnText>
              )}
            </VnBox>
          ))}
        </VnCard>
      )}
    </VnBox>
  );
};

type CoverageSummaryPropsType = {
  name: string;
  coverage: MemberCoverageType;
};

const CoverageSummary: FC<CoverageSummaryPropsType> = ({ name, coverage }) => {
  const { t } = useTranslator();

  const status = coverage.status;
  const coveredApplicationTypes = [
    ...new Set(coverage.coveredPrograms.map(p => p.applicationType)),
  ];
  const coveredPrograms = () => {
    const isCoveredByMedicaid = coveredApplicationTypes.includes("MEDICAID");
    const isCoveredByMedicare = coveredApplicationTypes.includes("MEDICARE");

    if (isCoveredByMedicaid && isCoveredByMedicare) {
      return t("native:coverage.programListing.medicareAndMedicaid");
    } else if (isCoveredByMedicaid) {
      return t("native:coverage.programListing.medicaid");
    } else if (isCoveredByMedicare) {
      return t("native:coverage.programListing.medicare");
    } else {
      return coveredApplicationTypes.join(t("common:listSeparator"));
    }
  };

  switch (status) {
    case "CHECKING":
      return (
        <VnBox className="items-start">
          {infoClause(
            t("native:coverage.lastCheck"),
            t("native:coverage.inProgress"),
          )}
        </VnBox>
      );
    case "COVERED":
      return (
        <VnText
          variant="lg"
          className="text-muted-foreground"
          i18nKey="native:coverage.isCovered"
          i18nOptions={{
            name,
            programs: coveredPrograms(),
          }}
        />
      );
    case "NOT_COVERED":
      return (
        <VnBox className="items-start">
          {infoClause(
            t("native:coverage.lastCheck"),
            coverage.lastChecked
              ? dateAsDayjs(coverage.lastChecked).format(
                  t("native:coverage.lastCheckDateFormat"),
                )
              : t("common:unknown"),
          )}
        </VnBox>
      );
    case "UNKNOWN":
      return (
        <VnBox className="w-full flex-col gap-4">
          <VnText
            variant="lg"
            className="text-muted-foreground"
            i18nKey="native:coverage.noMatchingRecord"
          />
        </VnBox>
      );
  }
};

type CoverageActionsPropsType = {
  coverage: MemberCoverageType;
  householdMemberId: string;
  onCoverageSelected: (coverage: CoverageDetailsType) => void;
};

const CoverageActions: FC<CoverageActionsPropsType> = ({
  coverage,
  householdMemberId,
  onCoverageSelected,
}) => {
  switch (coverage.status) {
    case "COVERED":
      return (
        <>
          {coverage.coveredPrograms.map(program => (
            <VnButton
              key={`${program.applicationType}-${program.workspaceId}-${program.payerId}`}
              variant="outline"
              className="w-full justify-between"
              i18nKey={getApplicationTypeLabel(program.applicationType)}
              onPress={() => onCoverageSelected(program)}
              iconPosition="right"
              IconComponent={ChevronRightIcon}
            />
          ))}
        </>
      );
    case "UNKNOWN":
      return (
        <VnLink
          renderAsButton
          buttonProps={{
            variant: "outline",
          }}
          i18nKey="native:coverage.updateProfileInformation"
          className="w-full"
          href={{
            pathname:
              "/(authed)/household/[householdMemberId]/profile-information",
            params: { householdMemberId },
          }}
        />
      );
    default:
      return null;
  }
};

type CoverageSectionPropsType = {
  householdMember: HouseholdMemberType;
};

export const CoverageSection: FC<CoverageSectionPropsType> = ({
  householdMember,
}) => {
  const { t } = useTranslator();
  const [selectedCoverage, setSelectedCoverage] =
    useState<CoverageDetailsType | null>(null);

  const coverage = householdMember?.coverage;

  const coverageText = (status: CoverageStatusEnumType) => {
    switch (status) {
      case "CHECKING":
        return t("native:coverage.status.checkingShort");
      case "UNKNOWN":
        return t("native:coverage.status.unknownShort");
      case "NOT_COVERED":
        return t("native:coverage.status.notCovered");
      case "COVERED":
        return t("native:coverage.status.covered");
    }
  };

  return (
    <VnCard className="items-start gap-4">
      <VnBox className="flex w-full flex-row items-center justify-between">
        <VnText variant="lgMedium" i18nKey="native:coverage.coverageStatus" />

        {coverage && (
          <VnBox className="flex-row items-center gap-1.5">
            <CoverageStatusIcon coverageStatus={coverage.status} />
            <VnText variant="md">{coverageText(coverage.status)}</VnText>
          </VnBox>
        )}
      </VnBox>

      {coverage && (
        <>
          <VnBox className="bg-background w-full rounded-md p-4">
            <CoverageSummary
              name={formatHouseholdMemberName(householdMember, "firstNameOnly")}
              coverage={coverage}
            />
          </VnBox>

          <CoverageActions
            coverage={coverage}
            householdMemberId={householdMember.id}
            onCoverageSelected={setSelectedCoverage}
          />
        </>
      )}

      <VnSheet
        open={Boolean(selectedCoverage)}
        shouldShowCloseButton={false}
        overlayClassName="z-0"
        onOpenChange={isOpen => {
          if (!isOpen) {
            setSelectedCoverage(null);
          }
        }}>
        <VnSheetContent className="md:max-w-1/3">
          <SideSheetHeader
            title={t("native:coverage.programCoverage", {
              program: selectedCoverage
                ? t(getApplicationTypeLabel(selectedCoverage.applicationType))
                : "",
            })}
            subtitle={formatHouseholdMemberName(householdMember, "fullName")}
          />
          {selectedCoverage && <CoverageDetails coverage={selectedCoverage} />}
        </VnSheetContent>
      </VnSheet>
    </VnCard>
  );
};
